import { useState } from 'react';

import PageHeader from 'components/ui/page-header';

import AvisoBrasilOpenBadge from './components/AvisoBrasilOpenBadge';
import ColecaoVazia from './components/ColecaoVazia';
import ErroDaColecao from './components/ErroDaColecao';
import FiltroDeCiclo from './components/FiltroDeCiclo';
import HeroDaColecao from './components/HeroDaColecao';
import AvisoDeFoto from './components/AvisoDeFoto';
import ListaDeSelos from './components/ListaDeSelos';
import ModoDaMoldura from './components/ModoDaMoldura';
import {
  anuncioDoFiltro,
  cicloMaisRecente,
  filtrarPorCiclo,
  filtroInicial,
  ModoDaImagem,
  opcoesDoFiltro,
  pedeFoto,
  proximoCiclo,
  rotuloDoTotal,
} from './selos-modelo';
import { useAtualizarFoto } from './useAtualizarFoto';
import { useColecaoDeSelos } from './useColecaoDeSelos';
import { useFocoNaTrocaDeFoto } from './useFocoNaTrocaDeFoto';

/** Galeria de selos: a coleção do funci em todos os ciclos, com filtro por ciclo e a imagem com ou sem a foto. */
export default function Selos() {
  const { carga, recarregar, ciclos, temFoto } = useColecaoDeSelos();
  // Trocar a foto aqui mesmo; com a foto nova, a coleção é relida e traz as molduras (AJH-48).
  const abrirEditorDeFoto = useAtualizarFoto();

  // Sem escolha do funci, o ciclo mais recente com selo (AJ2-18); a escolha sobrevive à releitura da coleção.
  const [escolhido, setEscolhido] = useState<string>();
  const [modo, setModo] = useState<ModoDaImagem>('com-foto');
  const [anuncio, setAnuncio] = useState('');

  const colecao = carga.estado === 'pronto' ? carga.dados : [];
  const refDaLista = useFocoNaTrocaDeFoto<HTMLUListElement>(temFoto, carga.estado === 'pronto' ? carga.dados : undefined);
  const filtro = escolhido ?? filtroInicial(colecao);
  const visiveis = filtrarPorCiclo(colecao, filtro);

  function escolherFiltro(novo: string) {
    setEscolhido(novo);
    setAnuncio(anuncioDoFiltro(filtrarPorCiclo(colecao, novo).length, novo));
  }

  return (
    <div className="mx-auto max-w-[1000px] px-4 pb-6 leading-[normal] lg:px-0">
      <PageHeader
        titulo="Galeria de selos"
        voltar={{ to: '/extrato', destino: 'o seu perfil' }}
        subtitulo={
          <>
            Sua coleção Joga Junto BB
            {carga.estado === 'pronto' && (
              <>
                {/* Separador decorativo: fica na cor do design (1,76:1), escondido do leitor de tela. */}
                <span aria-hidden="true" className="mx-3 align-[2px] font-text text-base font-normal text-line">
                  |
                </span>
                <span className="text-brand-blue">{rotuloDoTotal(colecao.length)}</span>
              </>
            )}
          </>
        }
      />

      <div aria-busy={carga.estado === 'carregando'} className="flex flex-col gap-[22px]">
        {carga.estado === 'carregando' && (
          <div role="status" className="flex flex-col gap-[22px]">
            <span className="sr-only">Carregando os seus selos</span>
            <div aria-hidden="true" className="h-[164px] animate-pulse rounded-jj-xl bg-surface-200 motion-reduce:animate-none" />
            <div aria-hidden="true" className="h-[265px] animate-pulse rounded-jj-xl bg-surface-200 motion-reduce:animate-none" />
          </div>
        )}

        {carga.estado === 'erro' && <ErroDaColecao onTentarDeNovo={recarregar} />}

        {carga.estado === 'pronto' && (
          <>
            <HeroDaColecao total={colecao.length} artes={colecao.slice(0, 3).flatMap(selo => (selo.arte ? [selo.arte] : []))} />

            {colecao.length === 0 ? (
              <ColecaoVazia proximoCiclo={proximoCiclo(cicloMaisRecente(ciclos.map(ciclo => ciclo.title)))} />
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <FiltroDeCiclo opcoes={opcoesDoFiltro(colecao)} filtro={filtro} onFiltro={escolherFiltro} />
                  <ModoDaMoldura modo={modo} onModo={setModo} />
                </div>
                {pedeFoto(visiveis, temFoto, modo) && <AvisoDeFoto onAtualizarFoto={abrirEditorDeFoto} />}
                <ListaDeSelos ref={refDaLista} selos={visiveis} temFoto={temFoto} modo={modo} onAtualizarFoto={abrirEditorDeFoto} />
              </>
            )}
          </>
        )}

        <span role="status" className="sr-only">
          {anuncio}
        </span>

        <AvisoBrasilOpenBadge />
      </div>
    </div>
  );
}
